<template>
  <v-container>
    <h1 class="text-h5 mb-4">{{ $t('settings.title') }}</h1>
    <v-card>
      <v-tabs v-model="currentTab" bg-color="primary">
        <v-tab value="main">{{ $t('settings.main') }}</v-tab>
        <v-tab value="icons">{{ $t('settings.icons') }}</v-tab>
      </v-tabs>

      <v-card-text>
        <v-window v-model="currentTab">
          <v-window-item value="main">
            <v-select
              v-model="language"
              :items="languageOptions"
              :label="$t('settings.language')"
              item-title="label"
              item-value="value"
              class="mb-4"
            />
            <v-select
              v-model="theme"
              :items="themes"
              :label="$t('settings.theme')"
              item-title="label"
              item-value="value"
              class="mb-4"
            />
            <v-select
              v-model="weekStart"
              :items="weekStartOptions"
              :label="$t('settings.weekStart')"
              item-title="label"
              item-value="value"
              class="mb-4"
            />
            <v-select
              v-model="timezone"
              :items="timezoneOptions"
              :label="$t('settings.timezone')"
              item-title="label"
              item-value="value"
              class="mb-4"
            />
          </v-window-item>

          <v-window-item value="icons">
            <v-card-title class="text-h6 mb-4">{{ $t('settings.navbarIcons') }}</v-card-title>
            <v-list>
              <v-list-item
                v-for="icon in filteredAvailableNavbarIcons"
                :key="icon.key"
                :prepend-icon="icon.icon"
                :title="$t(`nav.${icon.key}`)"
                :subtitle="icon.route"
              >
                <template v-slot:append>
                  <v-checkbox
                    v-model="enabledNavbarIcons"
                    :value="icon.key"
                    density="compact"
                    hide-details
                  />
                </template>
              </v-list-item>
            </v-list>
          </v-window-item>
        </v-window>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn @click="resetPreferences" variant="outlined">{{ $t('common.reset') }}</v-btn>
        <v-btn @click="savePreferences" color="primary" :loading="saving">{{ $t('common.save') }}</v-btn>
      </v-card-actions>
    </v-card>
    <v-snackbar v-model="showSnackbar" :timeout="3000" color="success">
      {{ $t('settings.savedSuccessfully') }}
    </v-snackbar>
  </v-container>
  </template>

<script setup lang="ts">
import { ref, watch, onMounted, computed } from 'vue'
import { useTheme } from 'vuetify'
import { useI18n } from 'vue-i18n'
import { apiGet, apiPut } from '../utils/api'

const { t, locale } = useI18n()

const themes = computed(() => [
  { label: t('settings.light'), value: 'light' },
  { label: t('settings.dark'), value: 'dark' },
])

const weekStartOptions = computed(() => [
  { label: t('settings.sunday'), value: 'sunday' },
  { label: t('settings.monday'), value: 'monday' },
])

const languageOptions = computed(() => [
  { label: t('settings.english'), value: 'en' },
  { label: t('settings.russian'), value: 'ru' },
])

const vTheme = useTheme()
const currentTab = ref('main')
const language = ref('en')
const theme = ref('light')
const weekStart = ref('sunday')
const timezone = ref('UTC')
const originalLanguage = ref('en')
const originalTheme = ref('light')
const originalWeekStart = ref('sunday')
const originalTimezone = ref('UTC')
const enabledNavbarIcons = ref<string[]>([])
const originalEnabledNavbarIcons = ref<string[]>([])
const isAdmin = ref(false)
const saving = ref(false)
const showSnackbar = ref(false)

// Define all available navbar icons
const availableNavbarIcons = computed(() => [
  { key: 'dashboard', icon: 'mdi-view-dashboard', route: '/', title: t('nav.dashboard') },
  { key: 'wallets', icon: 'mdi-wallet', route: '/wallets', title: t('nav.wallets') },
  { key: 'transactions', icon: 'mdi-swap-horizontal', route: '/transactions', title: t('nav.transactions') },
  { key: 'rates', icon: 'mdi-chart-line', route: '/rates', title: t('nav.rates') },
  { key: 'coins', icon: 'mdi-currency-btc', route: '/coins', title: t('nav.coins') },
  { key: 'exchanges', icon: 'mdi-swap-horizontal-bold', route: '/exchanges', title: t('nav.exchanges') },
  { key: 'expenses', icon: 'mdi-cash-minus', route: '/expenses', title: t('nav.expenses') },
  { key: 'expenseTypes', icon: 'mdi-tag-multiple', route: '/expense-types', title: t('nav.expenseTypes') },
  { key: 'investors', icon: 'mdi-account-group', route: '/investors', title: t('nav.investors') },
  { key: 'settlements', icon: 'mdi-cash-multiple', route: '/settlements', title: t('nav.settlements') },
  { key: 'courierTransfers', icon: 'mdi-truck-delivery', route: '/courier-transfers', title: t('nav.courierTransfers') },
  { key: 'walletOperations', icon: 'mdi-format-list-bulleted', route: '/wallet-operations', title: t('nav.walletOperations') },
  { key: 'settings', icon: 'mdi-cog', route: '/settings', title: t('nav.settings') },
  { key: 'users', icon: 'mdi-account-group', route: '/users', title: t('nav.users'), adminOnly: true },
  { key: 'roles', icon: 'mdi-shield-account', route: '/roles', title: t('nav.roles'), adminOnly: true },
  { key: 'permissions', icon: 'mdi-shield-key', route: '/permissions', title: t('nav.permissions'), adminOnly: true },
  { key: 'versioningConfigs', icon: 'mdi-history', route: '/versioning-configs', title: t('nav.versioningConfigs'), adminOnly: true },
])

// Filter available icons based on admin status
const filteredAvailableNavbarIcons = computed(() => {
  return availableNavbarIcons.value.filter(icon => !icon.adminOnly || isAdmin.value)
})

// Generate timezone options - common timezones
const timezoneOptions = [
  { label: 'UTC', value: 'UTC' },
  { label: 'America/New_York (EST/EDT)', value: 'America/New_York' },
  { label: 'America/Chicago (CST/CDT)', value: 'America/Chicago' },
  { label: 'America/Denver (MST/MDT)', value: 'America/Denver' },
  { label: 'America/Los_Angeles (PST/PDT)', value: 'America/Los_Angeles' },
  { label: 'America/Phoenix (MST)', value: 'America/Phoenix' },
  { label: 'Europe/London (GMT/BST)', value: 'Europe/London' },
  { label: 'Europe/Paris (CET/CEST)', value: 'Europe/Paris' },
  { label: 'Europe/Berlin (CET/CEST)', value: 'Europe/Berlin' },
  { label: 'Europe/Moscow (MSK)', value: 'Europe/Moscow' },
  { label: 'Asia/Dubai (GST)', value: 'Asia/Dubai' },
  { label: 'Asia/Tokyo (JST)', value: 'Asia/Tokyo' },
  { label: 'Asia/Shanghai (CST)', value: 'Asia/Shanghai' },
  { label: 'Asia/Hong_Kong (HKT)', value: 'Asia/Hong_Kong' },
  { label: 'Asia/Singapore (SGT)', value: 'Asia/Singapore' },
  { label: 'Australia/Sydney (AEDT/AEST)', value: 'Australia/Sydney' },
  { label: 'Australia/Melbourne (AEDT/AEST)', value: 'Australia/Melbourne' },
  { label: 'Pacific/Auckland (NZDT/NZST)', value: 'Pacific/Auckland' },
].sort((a, b) => {
  // Keep UTC at the top
  if (a.value === 'UTC') return -1
  if (b.value === 'UTC') return 1
  return a.label.localeCompare(b.label)
})

const loadUserPreferences = async () => {
  try {
    const response = await apiGet('/api/users/current')
    if (response.ok) {
      const userData = await response.json()
      const loadedLanguage = userData.language || localStorage.getItem('locale') || 'en'
      const loadedTheme = userData.theme || 'light'
      const loadedWeekStart = userData.week_start || 'sunday'
      const loadedTimezone = userData.timezone || 'UTC'
      const loadedNavbarIcons = userData.navbar_icons || []
      
      // Check if user is admin
      isAdmin.value = userData.roles?.some((r: any) => r.name === 'admin') || false
      
      // Set language
      language.value = loadedLanguage
      locale.value = loadedLanguage as 'en' | 'ru'
      localStorage.setItem('locale', loadedLanguage)
      
      theme.value = loadedTheme
      weekStart.value = loadedWeekStart
      timezone.value = loadedTimezone
      
      // Convert navbar_icons array to enabled keys array
      // If navbar_icons is empty or not set, default to all available icons
      if (Array.isArray(loadedNavbarIcons) && loadedNavbarIcons.length > 0) {
        // If it's an array of objects with keys, extract keys
        if (loadedNavbarIcons[0] && typeof loadedNavbarIcons[0] === 'object' && 'key' in loadedNavbarIcons[0]) {
          enabledNavbarIcons.value = loadedNavbarIcons.map((icon: any) => icon.key)
        } else if (typeof loadedNavbarIcons[0] === 'string') {
          // If it's an array of strings (keys)
          enabledNavbarIcons.value = loadedNavbarIcons
        } else {
          // Default to all available icons
          enabledNavbarIcons.value = filteredAvailableNavbarIcons.value.map(icon => icon.key)
        }
      } else {
        // Default to all available icons
        enabledNavbarIcons.value = filteredAvailableNavbarIcons.value.map(icon => icon.key)
      }
      
      originalLanguage.value = loadedLanguage
      originalTheme.value = loadedTheme
      originalWeekStart.value = loadedWeekStart
      originalTimezone.value = loadedTimezone
      originalEnabledNavbarIcons.value = [...enabledNavbarIcons.value]
      
      // Apply theme immediately
      vTheme.global.name.value = loadedTheme as any
      localStorage.setItem('theme', loadedTheme)
    }
  } catch (error) {
    console.error('Load user preferences error:', error)
  }
}

const savePreferences = async () => {
  saving.value = true
  try {
    // Convert enabled keys to full icon objects for storage
    const navbarIconsToSave = enabledNavbarIcons.value.map(key => {
      const icon = availableNavbarIcons.value.find(i => i.key === key)
      return icon ? { key: icon.key, icon: icon.icon, route: icon.route, title: icon.title } : null
    }).filter(Boolean) as Array<{ key: string; icon: string; route: string; title: string }>
    
    const response = await apiPut('/api/users/current', {
      user: { 
        language: language.value,
        theme: theme.value,
        week_start: weekStart.value,
        timezone: timezone.value,
        navbar_icons: navbarIconsToSave
      }
    })
    if (response.ok) {
      // Update original values
      originalLanguage.value = language.value
      originalTheme.value = theme.value
      originalWeekStart.value = weekStart.value
      originalTimezone.value = timezone.value
      originalEnabledNavbarIcons.value = [...enabledNavbarIcons.value]
      
      // Apply language immediately
      locale.value = language.value as 'en' | 'ru'
      localStorage.setItem('locale', language.value)
      window.dispatchEvent(new CustomEvent('locale-changed', { detail: { locale: language.value } }))
      
      // Dispatch event to reload navbar icons
      window.dispatchEvent(new CustomEvent('navbar-icons-changed'))
      
      // Apply theme immediately
      vTheme.global.name.value = theme.value as any
      localStorage.setItem('theme', theme.value)
      
      // Dispatch events to notify other components of the changes
      window.dispatchEvent(new CustomEvent('theme-changed', { detail: { theme: theme.value } }))
      window.dispatchEvent(new CustomEvent('week-start-changed', { detail: { weekStart: weekStart.value } }))
      window.dispatchEvent(new CustomEvent('timezone-changed', { detail: { timezone: timezone.value } }))
      
      // Show success message
      showSnackbar.value = true
    } else {
      const errorData = await response.json()
      console.error('Failed to save preferences:', errorData)
      alert('Failed to save preferences. Please try again.')
    }
  } catch (error) {
    console.error('Save preferences error:', error)
    alert('Error saving preferences. Please try again.')
  } finally {
    saving.value = false
  }
}

const resetPreferences = () => {
  language.value = originalLanguage.value
  theme.value = originalTheme.value
  weekStart.value = originalWeekStart.value
  timezone.value = originalTimezone.value
  enabledNavbarIcons.value = [...originalEnabledNavbarIcons.value]
  locale.value = originalLanguage.value as 'en' | 'ru'
  localStorage.setItem('locale', originalLanguage.value)
  vTheme.global.name.value = originalTheme.value as any
  localStorage.setItem('theme', originalTheme.value)
}

watch(language, (val) => {
  locale.value = val as 'en' | 'ru'
  localStorage.setItem('locale', val)
})

watch(theme, (val) => {
  vTheme.global.name.value = val as any
  localStorage.setItem('theme', val)
})

// initialize on mount
onMounted(() => {
  loadUserPreferences()
})
</script>


